import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/common/OptionSelect';
import { useVerifyMis } from '../hooks/useMpMutations';
import type { MpApprovalRow } from '../types/marketPromotion.types';

const CALL_OUTCOMES = [
  { id: 1, name: 'Reached, confirmed' },
  { id: 2, name: 'Reached, disputed' },
  { id: 3, name: 'No answer' },
] as const;

const TEACHER_ANSWERS = [
  { id: 1, name: 'Received the money' },
  { id: 2, name: 'Received less' },
  { id: 3, name: 'Received nothing' },
] as const;

interface RecordCallDialogProps {
  donation: MpApprovalRow | null;
  onOpenChange: (open: boolean) => void;
}

export function RecordCallDialog({ donation, onOpenChange }: RecordCallDialogProps) {
  return (
    <Dialog open={donation !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {donation === null ? null : (
          <CallForm
            key={donation.detailId}
            donation={donation}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CallForm({ donation, onDone }: { donation: MpApprovalRow; onDone: () => void }) {
  const [callStatus, setCallStatus] = useState<number | undefined>(
    donation.callStatus ?? undefined,
  );
  const [teacherFeedback, setTeacherFeedback] = useState<number | undefined>(
    donation.teacherFeedback ?? undefined,
  );
  const [misComment, setMisComment] = useState('');

  const verify = useVerifyMis(onDone);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Record the call</DialogTitle>
        <DialogDescription>
          {donation.itemName} · {donation.teacherName ?? 'the teacher'}{' '}
          {donation.teacherMobile ?? ''}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Call Outcome</span>
          <OptionSelect
            options={CALL_OUTCOMES}
            value={callStatus}
            onChange={setCallStatus}
            placeholder="What happened"
            clearable
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Teacher Said</span>
          <OptionSelect
            options={TEACHER_ANSWERS}
            value={teacherFeedback}
            onChange={setTeacherFeedback}
            placeholder="The answer"
            clearable
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Comment</span>
          <Input
            value={misComment}
            onChange={(event) => setMisComment(event.target.value)}
            placeholder="Anything worth recording"
            autoComplete="off"
          />
        </label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={verify.isPending}
          onClick={() =>
            verify.mutate({
              detailId: donation.detailId,
              payload: {
                ...(callStatus === undefined ? {} : { callStatus }),
                ...(teacherFeedback === undefined ? {} : { teacherFeedback }),
                ...(misComment.trim() === '' ? {} : { misComment: misComment.trim() }),
              },
            })
          }
        >
          {verify.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
